import { Spine } from "@esotericsoftware/spine-pixi-v7";
import * as PIXI from "pixi.js";
import TickerTimeout from "../../../utils/TickerTimeout";
import { SOUND_NAME } from "../../../sounds";
import { soundManager } from "../../../SoundManager";

// playOut never waits longer than its clip plus this (ms) — see playOut.
const OUT_SAFETY_MARGIN_MS = 500;

/**
 * HoldAndWinCounterController — Cleopatra's Respins counter.
 *
 * Reworked (Griffin's is gone) on PotCollectionRespinCounterController's
 * logic, which is the better one: the same clip names, the same queue.
 *
 * The spine is `hold_and_win_respin_counter` — the animator's own export
 * (2026-09-29), with its own atlas; the End Collector is a separate spine
 * (`hold_and_win_collector`). Its origin is the counter's centre and it sits
 * in the room's `respin_counter_placeholder` slot, which places and scales it;
 * nothing here positions it. Default skin (its layout skins are empty).
 *
 *   static       counter_full (3), counter_empty (0)
 *   transitions  counter_3to2 / 2to1 / 1to0          (a respin starts, −1)
 *                counter_0to1 / 0tofull / 1to2 / 1to3 / 2to3   (reset to 3)
 *   exit         counter_transform_0..3   (into the End Collector, later)
 *
 * playAnimation(n) — animate to n. A call while one is playing is queued and
 * coalesced to the LAST value, so the counter plays every clip fully and never
 * falls behind.
 * setValue(n) — show n with no animation and no sound (a reload restores the
 * server's value without replaying a decrease or reset — GDD §8.5).
 */

const SKELETON = "hold_and_win_respin_counter-skeleton";
// Pot Collection's tick. Not in this game's sound list yet — silent until it is.
// ── It is now: Zeus's own `respins_counter` (2026-10-22) ── and only a DECREASE
// ticks (3to2 / 2to1 / 1to0 — "when we deduct"); the reset to 3 is silent
// until it has a sound of its own.
const playTick = () => {
  if (SOUND_NAME.respins_counter) soundManager.play(SOUND_NAME.respins_counter);
};
// counter_3to2 -> true, counter_1to3 / counter_0tofull -> false.
const isDecrease = (animName) => {
  const m = /(\d)to(\d)$/.exec(animName || "");
  return !!m && Number(m[1]) > Number(m[2]);
};
const ATLAS = "hold_and_win_respin_counter-atlas";

class HoldAndWinCounterController {
  constructor(parentContainer) {
    this.parentContainer = parentContainer;
    this.counterSpine = null;
    this.initialized = false;
    this.isCleanedUp = false;

    // What is on screen now.
    this.displayedValue = null;

    this._isAnimating = false;
    this._animationQueue = [];
    this._onIdleCallbacks = [];
    // playOut's waiter and its safety — see _releaseOut.
    this._outResolve = null;
    this._outSafety = null;
    this._availableAnimations = null;
    this._hasAnimationCache = new Map();
    this._holdTimeout = null;

    this.initialize();
  }

  isReady() {
    return this.counterSpine?.state && this.initialized && !this.isCleanedUp;
  }

  initialize() {
    try {
      const assets = PIXI.Assets.cache;
      if (!assets.has(SKELETON) || !assets.has(ATLAS)) return;

      this.counterSpine = Spine.from({ skeleton: SKELETON, atlas: ATLAS });
      if (!this.counterSpine?.skeleton?.data?.animations || !this.counterSpine?.state) {
        return;
      }
      this.counterSpine.name = "hold_and_win_respin_counter";
      this._availableAnimations = this.counterSpine.skeleton.data.animations.map(
        (a) => a.name,
      );
      this.parentContainer.addChild(this.counterSpine);
      this.initialized = true;
      // Empty until the round shows it (showFull / setValue).
      this.setValue(0);
    } catch (error) {
      // Missing asset: the room plays on without a counter.
    }
  }

  hasAnimation(name) {
    if (this._hasAnimationCache.has(name)) return this._hasAnimationCache.get(name);
    const exists = this._availableAnimations?.includes(name) || false;
    this._hasAnimationCache.set(name, exists);
    return exists;
  }

  /** Resolves when nothing is playing and nothing is queued. */
  waitForIdle() {
    if (!this._isAnimating && this._animationQueue.length === 0) {
      return Promise.resolve();
    }
    return new Promise((resolve) => this._onIdleCallbacks.push(resolve));
  }

  /** Animate to `respinsLeft` (0–3). */
  playAnimation(respinsLeft) {
    if (!this.isReady()) return;
    if (this.displayedValue === respinsLeft && !this._isAnimating) return;
    if (this._isAnimating) {
      this._animationQueue.push(respinsLeft);
      return;
    }
    this._executeAnimation(respinsLeft);
  }

  /** 0 → 3, at the room's entry. */
  showFull() {
    if (!this.isReady()) return;
    this._animationQueue.length = 0;
    this._isAnimating = false;
    // A transition cut short releases its waiters (see setValue).
    this._releaseIdle();
    if (this.displayedValue === 3) return;
    this.displayedValue = 0;
    this._executeAnimation(3);
  }

  /**
   * Show `value` as it rests — no clip played through, no sound. 1 and 2
   * have no static clip, so they are the LAST frame of the transition that
   * ends on them (the pose a live −1 leaves on screen).
   */
  setValue(value) {
    if (!this.isReady()) return;
    this._clearHold();
    this._animationQueue.length = 0;
    this._isAnimating = false;
    // ── Forced to rest: whoever waits is RELEASED (2026-10-08) ── the
    // clearListeners() below drops the clip a waiter was counting on; left
    // waiting, the End Collector (waitForIdle, then playOut) never started.
    this._releaseOut();
    this._releaseIdle();

    const name =
      this._getStaticName(value) ??
      (this.hasAnimation(this._getTransitionName(value + 1, value))
        ? this._getTransitionName(value + 1, value)
        : null);
    if (!name || !this.hasAnimation(name)) return;
    try {
      const state = this.counterSpine.state;
      state.clearTracks();
      state.clearListeners();
      // Back to the setup pose first: counter_transform_N (playOut, into the
      // End Collector) shrinks the `attachment_spin_counter` bone to 0, and no
      // other clip keys it — without this the next round's counter stays at
      // scale 0, invisible (2026-10-02).
      this.counterSpine.skeleton.setToSetupPose();
      const entry = state.setAnimation(0, name, false);
      if (!this._getStaticName(value)) entry.trackTime = entry.animationEnd;
      state.update(0);
      state.apply(this.counterSpine.skeleton);
      this.displayedValue = value;
    } catch (_) {
      // Silent fail
    }
  }

  /**
   * Exit from the current state (counter_transform_N) — into the End
   * Collector, once that step exists.
   * @returns {Promise} resolves when the clip completes.
   */
  /**
   * @param {number} [timeScale=1] slower than authored when < 1 — the out
   *        clips are 0.167 s, too quick a shrink for the End Collector's
   *        handover (see endCollector).
   */
  playOut(timeScale = 1) {
    if (!this.isReady()) return Promise.resolve();
    this._animationQueue.length = 0;
    this._isAnimating = false;

    const outAnim = this._getOutName(this.displayedValue ?? 0);
    this.displayedValue = null;
    // The transition it cuts short releases its waiters (see setValue).
    this._releaseIdle();
    if (!this.hasAnimation(outAnim)) return Promise.resolve();
    // return new Promise((resolve) => {
    //   try {
    //     this.counterSpine.state.clearTracks();
    //     this.counterSpine.state.clearListeners();
    //     const outEntry = this.counterSpine.state.setAnimation(0, outAnim, false);
    //     if (outEntry) outEntry.timeScale = timeScale;
    //     this.counterSpine.state.addListener({
    //       complete: (entry) => {
    //         if (entry.animation.name === outAnim) resolve();
    //       },
    //     });
    //   } catch (_) {
    //     resolve();
    //   }
    // });
    // ── Never waits for ever (2026-10-08, the Zeus c11dfce9 hole) ── it
    // resolved only on its clip's `complete`, through a listener any
    // clearListeners() here wipes (setValue, a transition) — and the End
    // Collector awaits it. Now: `complete`, OR whoever replaces the clip
    // (_releaseOut), OR the clip's own length plus a margin.
    this._releaseOut();
    return new Promise((resolve) => {
      this._outResolve = resolve;
      try {
        this.counterSpine.state.clearTracks();
        this.counterSpine.state.clearListeners();
        const outEntry = this.counterSpine.state.setAnimation(0, outAnim, false);
        if (outEntry) outEntry.timeScale = timeScale;
        this.counterSpine.state.addListener({
          complete: (entry) => {
            if (entry.animation.name === outAnim) this._releaseOut();
          },
        });
        const clipMs =
          ((outEntry?.animation?.duration ?? 0) / (timeScale || 1)) * 1000;
        this._outSafety = new TickerTimeout(() => this._releaseOut(), clipMs + OUT_SAFETY_MARGIN_MS);
      } catch (_) {
        this._releaseOut();
      }
    });
  }

  /** The playOut waiter, released — at its `complete` or by anything that replaces it. */
  _releaseOut() {
    this._outSafety?.clear?.();
    this._outSafety = null;
    const done = this._outResolve;
    this._outResolve = null;
    done?.();
  }

  /** Every waitForIdle, released — the counter is at rest (or forced there). */
  _releaseIdle() {
    const cbs = this._onIdleCallbacks.splice(0);
    for (const cb of cbs) cb();
  }

  // ── internals (PotCollectionRespinCounterController) ──

  _executeAnimation(respinsLeft) {
    const from = this.displayedValue;

    if (from === null || from === undefined) {
      this.setValue(respinsLeft);
      this._processQueue();
      return;
    }
    if (from === respinsLeft) {
      this._processQueue();
      return;
    }

    const animName = this._getTransitionName(from, respinsLeft);
    if (this.hasAnimation(animName)) {
      this._isAnimating = true;
      this._playTransitionAnim(animName, respinsLeft);
    } else {
      // this._showStatic(respinsLeft, false);
      // No clip: the static, ticking only on a decrease (as the clips do).
      this._showStatic(respinsLeft, !(respinsLeft < from));
      this._processQueue();
    }
    this.displayedValue = respinsLeft;
  }

  /** Coalesce: drain the queue and go to its LAST value. */
  _processQueue() {
    this._isAnimating = false;
    if (this._animationQueue.length > 0) {
      const lastValue = this._animationQueue[this._animationQueue.length - 1];
      this._animationQueue.length = 0;
      this._executeAnimation(lastValue);
    } else {
      const cbs = this._onIdleCallbacks.splice(0);
      for (const cb of cbs) cb();
    }
  }

  _playTransitionAnim(animName, targetValue) {
    // Replaces whatever plays — a playOut still waiting is released.
    this._releaseOut();
    try {
      this.counterSpine.state.clearTracks();
      this.counterSpine.state.clearListeners();
      this.counterSpine.state.setAnimation(0, animName, false);
      // playTick();
      if (isDecrease(animName)) playTick();

      this.counterSpine.state.addListener({
        complete: (entry) => {
          if (entry.animation.name !== animName) return;
          // Settle on the static where there is one (0 and 3); 1 and 2 hold
          // the transition's last frame.
          this._showStatic(targetValue, true);
          // A short hold so the settled value reads before anything moves it
          // again — 100 ms, tuned in Pot Collection for these 167 ms clips.
          this._holdTimeout = new TickerTimeout(() => {
            this._holdTimeout = null;
            this._processQueue();
          }, 100);
        },
      });
    } catch (_) {
      this._showStatic(targetValue, false);
      this._processQueue();
    }
  }

  _showStatic(value, silent = false) {
    if (!this.isReady()) return;
    const animName = this._getStaticName(value);
    if (!animName || !this.hasAnimation(animName)) return;
    // Replaces whatever plays — a playOut still waiting is released.
    this._releaseOut();
    try {
      this.counterSpine.state.clearTracks();
      this.counterSpine.state.clearListeners();
      this.counterSpine.state.setAnimation(0, animName, false);
      if (!silent) playTick();
    } catch (_) {
      // Silent fail
    }
  }

  _clearHold() {
    if (this._holdTimeout) {
      this._holdTimeout.clear();
      this._holdTimeout = null;
    }
  }

  // "counter_FROMtoTO"; 0 → 3 is "counter_0tofull".
  _getTransitionName(from, to) {
    if (from === 0 && to === 3) return "counter_0tofull";
    return `counter_${from}to${to}`;
  }

  _getStaticName(value) {
    if (value === 0) return "counter_empty";
    if (value === 3) return "counter_full";
    return null;
  }

  _getOutName(value) {
    return `counter_transform_${value}`;
  }

  cleanup() {
    if (this.isCleanedUp) return;
    this.isCleanedUp = true;
    this._clearHold();
    // Torn down: nobody is left waiting on it.
    this._releaseOut();
    this._releaseIdle();

    if (this.counterSpine) {
      this.counterSpine.state?.clearListeners();
      this.counterSpine.state?.clearTracks();
      if (this.parentContainer?.children?.includes(this.counterSpine)) {
        this.parentContainer.removeChild(this.counterSpine);
      }
      this.counterSpine.destroy();
      this.counterSpine = null;
    }
    this._availableAnimations = null;
    this._hasAnimationCache.clear();
    this.displayedValue = null;
    this.initialized = false;

    const cbs = this._onIdleCallbacks.splice(0);
    for (const cb of cbs) cb();
    this._animationQueue.length = 0;
  }
}

export default HoldAndWinCounterController;
